import { useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { useContasCorrentes } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { ContasCorrentesTable } from '@/features/financeiro/components/ContasCorrentesTable';
import { ContaCorrenteDialog } from '@/features/financeiro/components/ContaCorrenteDialog';
import type { ContaCorrente, SalvarContaCorrenteRequest } from '@/features/financeiro/types';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const ContasCorrentes = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState<ContaCorrente | null>(null);
  const [busca, setBusca] = useState('');
  const buscaDebounced = useDebounce(busca, 400);

  const pag = usePaginacaoServidor({ resetarQuando: [buscaDebounced] });
  const { response, isLoading, criar, atualizar, inativar, isCriando, isAtualizando } = useContasCorrentes(buscaDebounced, pag.pagina, pag.tamanhoPagina);
  const contas = response?.itens ?? [];

  const handleOpenDialog = (conta?: ContaCorrente) => {
    setEditingConta(conta ?? null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConta(null);
  };

  const handleSubmit = (data: SalvarContaCorrenteRequest) => {
    if (editingConta) {
      atualizar({ id: editingConta.id, req: data }, { onSuccess: handleCloseDialog });
    } else {
      criar(data, { onSuccess: handleCloseDialog });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Contas Correntes</h1>
          <p className="text-muted-foreground">Gerencie as contas correntes</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />Nova Conta
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar conta corrente..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-10"
        />
      </div>

      <ContasCorrentesTable
        contas={contas}
        onEdit={handleOpenDialog}
        onInativar={(id) => inativar(id)}
      />

      <PaginacaoRodape {...pag.rodape(response)} />

      <ContaCorrenteDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingConta={editingConta}
        isLoading={isCriando || isAtualizando}
      />
    </div>
  );
};

export default ContasCorrentes;
